import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin, Bed, Bath, Users, Zap, Star, Crown, AlertTriangle } from 'lucide-react';
import { Badge } from '../components/ui/badge';

export const PropertyCard = ({ property, onSave, isSaved = false, showOwner = false }) => {
  const {
    property_id,
    title,
    address,
    city,
    price_per_month,
    bedrooms,
    bathrooms,
    max_guests,
    property_type,
    instant_booking,
    featured_badge,
    is_demo,
    photos,
    average_rating,
    review_count,
    owner_name,
    owner_picture
  } = property;

  const primaryPhoto = photos?.[0] || 'https://images.unsplash.com/photo-1597665863042-47e00964d899?w=800';

  const propertyTypeLabels = {
    room: 'Private Room',
    adu: 'ADU',
    guesthouse: 'Guesthouse',
    apartment: 'Apartment'
  };

  return (
    <Link 
      to={`/property/${property_id}`}
      className={`property-card group bg-white rounded-xl border overflow-hidden hover:shadow-xl transition-all duration-300 block ${
        is_demo 
          ? 'border-amber-400 ring-2 ring-amber-100'
          : featured_badge 
            ? 'border-amber-300 ring-2 ring-amber-100' 
            : 'border-slate-100 hover:border-blue-100'
      }`}
      data-testid={`property-card-${property_id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img 
          src={primaryPhoto} 
          alt={title}
          className="property-image w-full h-full object-cover"
        />
        
        {/* Save Button */}
        {onSave && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onSave(property_id);
            }}
            className="absolute top-3 right-3 p-2 bg-white/90 rounded-full shadow-md hover:bg-white transition-colors"
            data-testid={`save-property-${property_id}`}
          >
            <Heart 
              className={`w-5 h-5 ${isSaved ? 'fill-coral text-coral' : 'text-slate-600'}`} 
            />
          </button>
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          {is_demo && (
            <Badge className="bg-amber-500 text-white" data-testid={`demo-badge-${property_id}`}>
              <AlertTriangle className="w-3 h-3 mr-1" />
              DEMO
            </Badge>
          )}
          {featured_badge && !is_demo && (
            <Badge className="bg-amber-500 text-white" data-testid={`featured-badge-${property_id}`}>
              <Crown className="w-3 h-3 mr-1" />
              Featured
            </Badge>
          )}
          <Badge className="bg-white/90 text-slate-700 hover:bg-white">
            {propertyTypeLabels[property_type] || property_type}
          </Badge>
          {instant_booking && (
            <Badge className="bg-trust-blue text-white">
              <Zap className="w-3 h-3 mr-1" />
              Instant
            </Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading font-semibold text-slate-900 line-clamp-1 group-hover:text-trust-blue transition-colors">
            {title}
          </h3>
          {average_rating > 0 && (
            <div className="flex items-center gap-1 text-sm shrink-0">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="font-medium">{average_rating.toFixed(1)}</span>
              <span className="text-slate-400">({review_count})</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 text-slate-500 text-sm mb-3">
          <MapPin className="w-4 h-4" />
          <span className="line-clamp-1">{address}, {city}</span>
        </div>

        <div className="flex items-center gap-4 text-slate-600 text-sm mb-4">
          <span className="flex items-center gap-1">
            <Bed className="w-4 h-4" />
            {bedrooms} {bedrooms === 1 ? 'bed' : 'beds'}
          </span>
          <span className="flex items-center gap-1">
            <Bath className="w-4 h-4" />
            {bathrooms} {bathrooms === 1 ? 'bath' : 'baths'}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            {max_guests} {max_guests === 1 ? 'guest' : 'guests'}
          </span>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div>
            <span className="font-heading font-bold text-xl text-slate-900">
              ${price_per_month.toLocaleString()}
            </span>
            <span className="text-slate-500 text-sm">/month</span>
          </div>
          
          {showOwner && owner_name && (
            <div className="flex items-center gap-2">
              {owner_picture ? (
                <img src={owner_picture} alt={owner_name} className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-trust-blue flex items-center justify-center text-white text-xs">
                  {owner_name.charAt(0)}
                </div>
              )}
              <span className="text-sm text-slate-600">{owner_name.split(' ')[0]}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
